<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { useRoute } from 'vue-router';
import CalendarBulkAttendance from './CalendarBulkAttendance.vue';
import { CALENDAR_COLOR, EMIT, PERMISSIONS } from '@/enum';
import { useCalendarBulkAttendanceStore, useCalendarStore } from '@/store';
import { can, useDateTime, modifiedRRulePlugin, usePersonTypes } from '@/composables';

const emit = defineEmits<{
  (eventName: EMIT.DOCUMENT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.EDIT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.NEW_EVENT, info: DateClickArg): void;
  (eventName: EMIT.UPDATE_DAY, date: string, month: string, year: number): void;
  (eventName: EMIT.MOUNTED, info: string): void;
}>();

const calendar = ref();
const calendarStore = useCalendarStore();
const calendarBulkAttendanceStore = useCalendarBulkAttendanceStore();
const route = useRoute();
defineExpose({ calendar });

onMounted(() => {
  init();
});

const init = () => {
  if (route.path.includes('/calendar/day')) {
    setCalendarEvents();
  }

  if (calendarStore.redirectedToDate && calendar.value?.getApi) {
    calendar.value?.getApi().changeView('timeGridDay', calendarStore.redirectedToDate);
  }
};
watch(() => route.params, init);

const setCalendarEvents = () => (calendarOptions.value.events = calendarStore.events || []);

watch(() => calendarStore.events, setCalendarEvents);

const calendarOptions = ref<CalendarOptions>({
  plugins: [timeGridPlugin, interactionPlugin, modifiedRRulePlugin],
  initialView: 'timeGridDay',
  headerToolbar: false,
  allDaySlot: false,
  dayHeaderFormat: { day: 'numeric' },
  dayHeaderContent: (arg) =>
    calendarBulkAttendanceStore.defineBulkAttendance(
      arg,
      can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE]) && !usePersonTypes.isCaseManager(),
    ),
  nowIndicator: true,
  weekends: true,
  height: '100%',
  expandRows: true,
  eventClassNames: (arg) => calendarStore.defineEventsClassNames(arg),
  eventShortHeight: 36,
  eventBackgroundColor: CALENDAR_COLOR.EVENT_BACKGROUND_COLOR,
  eventBorderColor: CALENDAR_COLOR.EVENT_BORDER_COLOR,
  eventTextColor: CALENDAR_COLOR.EVENT_TEXT_COLOR,
  eventMinHeight: 24,
  events: [],
  eventClick: (info: EventClickArg) => {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      emit(EMIT.DOCUMENT_EVENT, info);
    } else {
      emit(EMIT.EDIT_EVENT, info);
    }
  },
  dateClick: function (info: DateClickArg) {
    emit(EMIT.NEW_EVENT, info);
  },
  scrollTime: '07:00:00',
  viewDidMount: () => emit(EMIT.MOUNTED, 'day'),
});
</script>

<template>
  <div class="calendar-page-content">
    <CalendarBulkAttendance
      v-if="
        can([PERMISSIONS.THERAPY_RESULTS_BULK_UPDATE]) &&
        !usePersonTypes.isCaseManager() &&
        calendarBulkAttendanceStore.displayBulkAttendance
      "
      @cancel="calendarBulkAttendanceStore.onBulkAttendanceCancel"
    />
    <div class="calendar-page-day-view">
      <FullCalendar ref="calendar" :options="calendarOptions">
        <template #eventContent="arg">
          <div class="fc-event-main-frame">
            <div class="fc-event-time">
              {{ useDateTime().formatTimeByTimezone(arg.event.start, arg.event.extendedProps.hostTimezoneName) }} -
              {{ useDateTime().formatTimeByTimezone(arg.event.end, arg.event.extendedProps.hostTimezoneName) }}
            </div>
            <div class="fc-event-title-container">
              <div class="fc-event-title fc-sticky">{{ arg.event.title }}</div>
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/theme/extensions/calendar';

.calendar-page-day-view {
  width: 100%;
  padding-top: 2px;
}

.title-day {
  margin-left: 30px;
}
.timezone-day {
  margin-left: -5px;
}
</style>
